import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getInitials } from '@/types/schedule';
import { cn } from '@/lib/utils';

export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_CLASS: Record<UserAvatarSize, string> = {
    xs: 'h-5 w-5 text-[10px]',
    sm: 'h-6 w-6 text-xs',
    md: 'h-8 w-8 text-sm',
    lg: 'h-10 w-10 text-sm',
    xl: 'h-12 w-12 text-base',
    '2xl': 'h-16 w-16 text-lg',
};

export interface UserAvatarProps {
    /** 头像图片地址；为空或加载失败(如 img.clerk.com 415)时落回首字母。 */
    src?: string | null;
    /** 用户姓名，用于生成首字母兜底。 */
    name?: string | null;
    /** img alt 文案，缺省用 name。 */
    alt?: string;
    /** 预设尺寸；需要精确像素时用 className / style 覆盖。 */
    size?: UserAvatarSize;
    /** 追加到 Avatar 根节点(可覆盖尺寸/加 ring/border 等)。 */
    className?: string;
    /** 追加到首字母兜底节点(覆盖底色/字色/字号)。 */
    fallbackClassName?: string;
    /** 兜底节点的行内样式(渐变底等 className 表达不了的场景)。 */
    fallbackStyle?: React.CSSProperties;
    /** Avatar 根节点行内样式(像素级尺寸场景)。 */
    style?: React.CSSProperties;
    /** 可选角标 slot(在线点等)，绝对定位由调用方自带；提供时外层包 relative 容器。 */
    badge?: React.ReactNode;
}

/**
 * 统一的用户头像原子组件：Radix Avatar + 首字母兜底。
 *
 * 与裸 `<img>` + 三元判 URL 的写法不同，Radix 的 AvatarImage 只在图片
 * 真正加载成功后才展示 —— URL 存在但加载失败(403/415/网络错误)时自动
 * 落到 AvatarFallback，不会出现浏览器碎图。
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
    src,
    name,
    alt,
    size = 'md',
    className,
    fallbackClassName,
    fallbackStyle,
    style,
    badge,
}) => {
    const trimmedName = (name || '').trim();
    const avatar = (
        <Avatar className={cn(SIZE_CLASS[size], className)} style={style}>
            {src ? <AvatarImage src={src} alt={alt ?? (trimmedName || 'avatar')} className="object-cover" /> : null}
            <AvatarFallback
                className={cn('bg-primary font-medium text-primary-foreground', fallbackClassName)}
                style={fallbackStyle}
            >
                {getInitials(trimmedName)}
            </AvatarFallback>
        </Avatar>
    );
    if (!badge) return avatar;
    return (
        <span className="relative inline-block shrink-0">
            {avatar}
            {badge}
        </span>
    );
};

export default UserAvatar;
